import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Server, ShieldCheck, KeyRound, GitBranch, Globe } from 'lucide-react';
import { Container, Eyebrow, Reveal, Btn } from './landing/primitives';

const features = [
  { icon: Server, title: 'Dedicated infrastructure', desc: 'Private compute clusters with guaranteed SLAs and custom model hosting.' },
  { icon: ShieldCheck, title: 'Compliance ready', desc: 'SOC 2, GDPR and HIPAA alignment with detailed audit logging.' },
  { icon: KeyRound, title: 'Granular access control', desc: 'Usage limits, key scoping and real-time monitoring dashboards.' },
  { icon: GitBranch, title: 'Custom workflows', desc: 'Complex AI pipelines with branching logic and human-in-the-loop approvals.' },
  { icon: Globe, title: 'Global edge network', desc: 'Deploy models closer to users with a worldwide inference network.' },
];

const Enterprise = () => {
  const navigate = useNavigate();
  return (
    <section className="py-24 lg:py-32 border-t border-line bg-app-alt" id="enterprise" data-testid="enterprise-section">
      <Container>
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <Reveal className="lg:col-span-5 lg:sticky lg:top-32">
            <Eyebrow>Enterprise</Eyebrow>
            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-heading font-medium tracking-tight leading-[1.08] text-content">
              AI at any scale.<br />On your terms.
            </h2>
            <p className="mt-6 text-content-2 text-base leading-relaxed max-w-md">
              Engineered for organizations that demand performance, security and reliability at every level — from seed-stage to Fortune 500.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Btn onClick={() => navigate('/chat')} data-testid="enterprise-cta-btn">
                Contact Sales <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Btn>
              <Btn variant="secondary" onClick={() => navigate('/docs')} data-testid="enterprise-docs-btn">View documentation</Btn>
            </div>
          </Reveal>

          <div className="lg:col-span-7">
            {features.map((f, idx) => {
              const Icon = f.icon;
              return (
                <Reveal key={f.title} delay={idx * 0.05} className="group flex gap-6 py-7 border-t border-line last:border-b hover:border-line-2 transition-colors duration-300" data-testid={`enterprise-feature-${idx}`}>
                  <Icon className="w-5 h-5 mt-0.5 flex-shrink-0 text-content-3 group-hover:text-content transition-colors duration-300" strokeWidth={1.25} />
                  <div className="grid sm:grid-cols-12 gap-2 sm:gap-6 flex-1">
                    <h4 className="sm:col-span-5 text-base font-heading font-medium tracking-tight text-content">{f.title}</h4>
                    <p className="sm:col-span-7 text-content-2 text-sm leading-relaxed">{f.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </Container>
    </section>
  );
};

export default Enterprise;
